import { useEffect, useState } from "react";
import { CampionatiService } from "../services/campionatiService";
import { PublicApiService } from "../services/publiApiService";
import { Campionato } from "../types/campionati/campionato";
import { CampionatoRanking } from "../types/campionati/campionatoRanking";
import { FishingDiscipline, formatKg, useFishingDiscipline, disciplineLabel } from "../types/fishingDiscipline";
import PublicDisciplineLayout from "../components/publicSite/PublicDisciplineLayout";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import LeagueRankingList from "../components/LeagueRankingList";
import Sponsors from "../components/publicSite/Sponsors";
import logoBase64 from "../assets/logoBase64";
import sponsor1 from "../assets/sponsors/sponsor1";
import sponsor2 from "../assets/sponsors/sponsor2";
import sponsor3 from "../assets/sponsors/sponsor3";
import armeriaSollevanti from "../assets/sponsors/armeria-sollevanti.png";

export default function LeagueStandingsPage({ publicView = false }: { publicView?: boolean }) {
  const discipline = useFishingDiscipline();
  const carp = discipline === FishingDiscipline.CarpAndCrucian;
  const title = `${publicView ? "Campionati" : "Classifiche"} ${disciplineLabel(discipline)}`;
  const [leagues, setLeagues] = useState<Campionato[]>([]);
  const [selected, setSelected] = useState("");
  const [ranking, setRanking] = useState<CampionatoRanking | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingLeagues, setLoadingLeagues] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setLoadingLeagues(true); setError("");
    const request = publicView ? PublicApiService.getLeagues(discipline)
      : CampionatiService.getCampionatiList(1, 1000, undefined, discipline);
    request.then(response => {
      if (!active) return;
      const items: Campionato[] = response.results || [];
      setLeagues(items);
      setSelected(items.length ? String(items.reduce((a, b) => a.id > b.id ? a : b).id) : "");
    }).catch(e => { if (active) setError(e.message || "Impossibile caricare i campionati."); })
      .finally(() => { if (active) setLoadingLeagues(false); });
    return () => { active = false; };
  }, [publicView, discipline]);
  useEffect(() => {
    if (!selected) { setRanking(null); return; }
    let active = true;
    setLoading(true); setError(""); setRanking(null);
    const request = publicView ? PublicApiService.getCampionatoRanking(Number(selected))
      : CampionatiService.getCampionatoRanking(Number(selected));
    request.then(data => { if (active) setRanking(data); })
      .catch(e => { if (active) setError(e.message || "Impossibile caricare la classifica."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selected, publicView]);
  const exportPdf = async () => {
    if (!ranking) return;
    let carpSponsor: Uint8Array | undefined;
    if (carp) {
      try {
        const response = await fetch(armeriaSollevanti);
        if (!response.ok) throw new Error("Sponsor image unavailable");
        carpSponsor = new Uint8Array(await response.arrayBuffer());
      } catch {
        setError("Impossibile caricare l’immagine dello sponsor. Riprova a esportare il PDF.");
        return;
      }
    }
    setError("");
    const doc = new jsPDF({ orientation: "landscape" });
    const leagueName = leagues.find(l => String(l.id) === selected)?.name || "Campionato";
    doc.addImage(logoBase64, "PNG", 10, 8, 24, 32);
    doc.setFontSize(12);
    doc.text("Con la gentile collaborazione di:", doc.internal.pageSize.getWidth() - 90, 14);
    if (carpSponsor) {
      doc.addImage(carpSponsor, "PNG", doc.internal.pageSize.getWidth() - 62, 18, 36, 27, undefined, "FAST");
    } else {
      [sponsor1, sponsor2, sponsor3].forEach((sponsor, index) => doc.addImage(sponsor, "JPEG", doc.internal.pageSize.getWidth() - 130 + index * 40, 18, 36, 24));
    }
    doc.setFontSize(16);
    doc.text(doc.splitTextToSize(leagueName, 110), 44, 22);
    doc.setFontSize(10);
    doc.text(disciplineLabel(discipline), 44, 41);
    autoTable(doc, { startY: 48,
      head: [["Pos.", "Concorrente", "Punti", "Primi", "Secondi", "Terzi", carp ? "Kg totali" : "Totale punti gare", ...ranking.competitions.map(c => c.name)]],
      body: ranking.competitors.map((c, index) => [carp ? c.stats.position ?? "—" : index + 1, `${c.lastName} ${c.firstName}`, c.stats.totalPoints,
        c.stats.firstPlaces, c.stats.secondPlaces, c.stats.thirdPlaces, carp ? formatKg(c.stats.totalWeightKg ?? 0) : c.stats.totalPointsCompetitions ?? 0,
        ...ranking.competitions.map(competition => {
          const result = c.perRace.find(r => String(r.raceId) === String(competition.id));
          return !carp ? String(result?.pointsAwarded ?? 0) : result?.isAbsent ? "Assente (0)" : result?.participated ? String(result.pointsAwarded) : "—";
        })]),
      theme: "grid", styles: { fontSize: 9, halign: "center" }, headStyles: { fillColor: [41, 128, 185], textColor: 255 }, margin: { left: 10, right: 10 }
    });
    doc.save(`classifica-campionato-${carp ? "carpe" : "trout"}.pdf`);
  };
  const content = <div className="standings-content">
    {!publicView && <h1 className="text-3xl font-bold">{title}</h1>}
    <div className="standings-controls"><label className="site-field"><span>Campionato</span>
      <select disabled={loadingLeagues}
        value={selected} onChange={e => setSelected(e.target.value)}>
        <option value="">Seleziona un campionato</option>
        {leagues.map(l => <option key={l.id} value={l.id}>{l.name} ({l.year})</option>)}
      </select>
    </label>
    {!loading && ranking && ranking.competitors.length > 0 && <button type="button" className="site-button site-button--secondary" onClick={exportPdf}>Esporta PDF ↓</button>}
    </div>
    {error && <p role="alert" className="site-notice site-notice--error">{error}</p>}
    {(loading || loadingLeagues) && <p role="status" className="site-notice">Caricamento classifica…</p>}
    {!loadingLeagues && !leagues.length && !error && <p className="site-notice">Non ci sono ancora campionati {disciplineLabel(discipline)}.</p>}
    {!loading && ranking && <>
      {ranking.competitors.length > 0 && <p className="site-note">Seleziona un concorrente per vedere i risultati delle singole gare.</p>}
      <LeagueRankingList key={selected} ranking={ranking} discipline={discipline} compactDetails />
      {carp && <p className="site-note">Contano le gare pubblicate. A pari punti: primi, secondi, terzi posti, poi peso totale pescato.</p>}
    </>}
    {publicView && <Sponsors discipline={discipline} />}
  </div>;
  return publicView ? <PublicDisciplineLayout title={title} intro="Segui la classifica del campionato e i risultati di ogni gara.">{content}</PublicDisciplineLayout> : content;
}
